// Core
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import {
  Crumbs,
  DateRangePopover,
  FieldSearchSelect,
  LineChart,
  PageHead,
  RemoteTable,
  SectionCard,
  Tabs,
} from "components/AtomsV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectTimeSelector, updateTimeSelector } from "state/application/applicationSlice";
import {
  useGetOMIEDataBetweenDatesQuery,
  useGetOMIEDataFilteredQuery,
} from "state/omie/omieApiSlice";

// Utils
import { createChartXAxisLabel } from "utils/charts";
import { datetimeToDate, datetimeToTime } from "utils/time";

const num = (v) => <span className="tabular-nums">{v != null ? Number(v).toFixed(2) : "—"}</span>;

function ReadOMIE() {
  const dispatch = useDispatch();
  const { t } = useTranslation(["omie", "common"]);
  const { granularity, startDate, endDate, horizon, isExpanded } = useSelector(selectTimeSelector);
  // OMIE prices only exist at hourly resolution or coarser; never request 15min.
  const omieGranularity = granularity === "MIN15" ? "HOUR" : granularity;

  const [tab, setTab] = useState("charts");
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);

  const { data: analytics } = useGetOMIEDataBetweenDatesQuery(
    { startDate, endDate, granularity: omieGranularity },
    { refetchOnMountOrArgChange: true }
  );
  const { data: tableData, isFetching } = useGetOMIEDataFilteredQuery(
    {
      page,
      limit,
      date,
      time,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    { refetchOnMountOrArgChange: true }
  );

  const rowsAnalytics = analytics ?? [];
  const timestamps = useMemo(() => rowsAnalytics.map((r) => r.datetime_timestamp), [rowsAnalytics]);
  const labels = useMemo(
    () => rowsAnalytics.map((r) => createChartXAxisLabel(r.datetime_timestamp, omieGranularity)),
    [rowsAnalytics, omieGranularity]
  );
  const series = useMemo(
    () => [
      {
        name: t("analytics.dataset.price_label"),
        data: rowsAnalytics.map((r) => (r.price != null ? +Number(r.price).toFixed(2) : null)),
        color: "var(--accent)",
      },
    ],
    [rowsAnalytics, t]
  );

  const tableRows = useMemo(
    () =>
      (tableData?.data ?? []).map((r, i) => ({
        id: `${r.datetime_timestamp}-${i}`,
        date: datetimeToDate(r.datetime_timestamp),
        time: datetimeToTime(r.datetime_timestamp),
        price: r.price,
        adjustment: r.adjustment,
      })),
    [tableData]
  );

  const columns = [
    { key: "date", label: t("table.columns.date"), sortable: true, width: "16%" },
    { key: "time", label: t("table.columns.time"), sortable: true, width: "12%" },
    { key: "price", label: t("table.columns.price"), align: "right", render: num },
    { key: "adjustment", label: t("table.columns.adjustment"), align: "right", render: num },
  ];

  const allLabel = t("filters.all", { defaultValue: "Todos", ns: "common" });
  const dateOptions = [
    { value: "", label: allLabel },
    ...(tableData?.distinct_dates ?? []).map((o) => ({ value: o.date, label: o.date })),
  ];
  const timeOptions = [
    { value: "", label: allLabel },
    ...(tableData?.distinct_times ?? []).map((o) => ({ value: o.time, label: o.time })),
  ];
  const resetPage = () => setPage(0);

  const filters = (
    <>
      <FieldSearchSelect
        label={t("table.columns.date")}
        value={date ?? ""}
        onChange={(v) => {
          resetPage();
          setDate(v || null);
        }}
        options={dateOptions}
      />
      <FieldSearchSelect
        label={t("table.columns.time")}
        value={time ?? ""}
        onChange={(v) => {
          resetPage();
          setTime(v || null);
        }}
        options={timeOptions}
      />
    </>
  );

  return (
    <div>
      <Crumbs items={[{ label: t("crumb") }, { label: t("title") }]} />
      <PageHead
        title={t("title")}
        subtitle={t("subtitle")}
        actions={
          <DateRangePopover
            value={{ start: startDate, end: endDate }}
            onChange={(v) =>
              dispatch(
                updateTimeSelector({
                  startDate: v.start,
                  endDate: v.end,
                  granularity,
                  horizon,
                  isExpanded,
                })
              )
            }
          />
        }
      />

      <Tabs
        tabs={[
          { id: "charts", label: t("tabs.charts"), icon: "show_chart" },
          { id: "table", label: t("tabs.table"), icon: "table_rows" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "charts" ? (
        <SectionCard id="omie-price" icon="show_chart" title={t("analytics.title_price")}>
          <div className="text-[12px] text-ink-3 mb-2">{t("analytics.description_price")}</div>
          <div className="flex items-center gap-[6px] text-[12px] text-ink-2 mb-2">
            <i
              className="inline-block w-[9px] h-[9px] rounded-[3px]"
              style={{ background: "var(--accent)" }}
            />
            {t("analytics.dataset.price_label")}
          </div>
          <LineChart
            series={series}
            labels={labels}
            timestamps={timestamps}
            unit="€"
            height={340}
            type="area"
            yTitle="€/MWh"
            showLegend={false}
          />
        </SectionCard>
      ) : (
        <div className="rec-card">
          <RemoteTable
            columns={columns}
            rows={tableRows}
            totalCount={tableData?.total_count ?? 0}
            page={page}
            limit={limit}
            onPageChange={setPage}
            sort={sortOrder}
            onSortChange={(s) => {
              setPage(0);
              setSortOrder(s);
            }}
            filters={filters}
            loading={isFetching}
          />
        </div>
      )}
    </div>
  );
}

export default ReadOMIE;
